/**
 * 移动端 Web 页面跳转函数
 * 
 * 根据当前环境自动选择合适的跳转方式：
 * - 在同花顺/iFind/期货客户端中，使用客户端协议跳转
 * - 在其他环境中，使用普通 URL 跳转
 * 
 * @param url 要跳转的 URL
 * @param title 页面标题（可选）
 * @param isFullScreen 是否全屏显示（可选，默认 false）
 * @returns void
 */
import { isInThsApp } from '../../base/env/app/isInThsApp';
import { isInIFindApp } from '../../base/env/app/isInIFindApp';
import { isInFuturesApp } from '../../base/env/app/isInFuturesApp';
import { getPlatform } from '../../base/env/platform/getPlatform';

/**
 * 移动端 Web 页面跳转函数参数
 */
export interface JumpWebOptions {
  /** 要跳转的 URL */
  url: string;
  /** 页面标题 */
  title?: string;
  /** 是否全屏显示 */
  isFullScreen?: boolean;
}

/**
 * 移动端 Web 页面跳转
 * 
 * @param options 跳转选项
 */
export function jumpWeb(options: JumpWebOptions): void;
/**
 * 移动端 Web 页面跳转 (兼容旧版本)
 * 
 * @param url 要跳转的 URL
 * @param title 页面标题（可选）
 * @param isFullScreen 是否全屏显示（可选，默认 false）
 */
export function jumpWeb(url: string, title?: string, isFullScreen?: boolean): void;
export function jumpWeb(
  urlOrOptions: string | JumpWebOptions,
  title?: string,
  isFullScreen?: boolean
): void {
  // 处理参数
  let options: JumpWebOptions;
  
  if (typeof urlOrOptions === 'string') {
    // 兼容旧版本调用方式
    options = {
      url: urlOrOptions,
      title,
      isFullScreen
    };
  } else {
    options = urlOrOptions;
  }
  
  const { url, title: optTitle = '', isFullScreen: optIsFullScreen = false } = options;
  
  if (!url) {
    console.warn('url is invalid');
    return;
  }

  // 已存在客户端协议或非客户端环境，直接跳转
  if (url.startsWith('client.html') || (!isInThsApp() && !isInIFindApp() && !isInFuturesApp())) {
    window.location.href = url;
    return;
  }

  // 处理 URL 格式
  const { protocol } = window.location;
  const fullUrl = url.startsWith('http') ? url : `${protocol}${url}`;
  const titleParam = optTitle ? `^title=${optTitle}` : '';
  
  // 根据是否全屏选择不同的跳转方式
  if (optIsFullScreen) {
    jumpFullScreen(fullUrl);
  } else {
    jumpNormal(fullUrl, titleParam);
  }
}

/**
 * 普通模式跳转
 */
function jumpNormal(url: string, titleParam: string): void {
  if (isInFuturesApp()) {
    const webid = getPlatform() === 'iphone' ? '2804' : '2817';
    window.location.href = `client.html?action=ymtz^webid=${webid}^url=${url}${titleParam}`;
  } else if (isInThsApp() || isInIFindApp()) {
    window.location.href = `client.html?action=ymtz^webid=2804^mode=new^url=${url}${titleParam}`;
  } else {
    window.location.href = url;
  }
}

/**
 * 全屏模式跳转
 */
function jumpFullScreen(url: string): void {
  if (isInFuturesApp()) {
    // 期货跳转全面屏
    window.location.href = `client.html?action=ymtz^webid=2804^mode=new^ishiddenbar=1^url=${url}`;
  } else if (isInThsApp() || isInIFindApp()) {
    if (getPlatform() === 'iphone' || getPlatform() === 'harmony') {
      window.location.href = `client.html?action=ymtz^webid=2305^showStatusBar=1^StayHighForStatusBar=0^url=${url}`;
    } else {
      window.location.href = `client.html?action=ymtz^webid=2804^mode=new^fullscreen=1^notitlebar=1^url=${url}`;
    }
  } else {
    window.location.href = url;
  }
} 